import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, Sparkles, Loader2, RotateCw, Award, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

export default function BatchPassport() {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [recalcBusy, setRecalcBusy] = useState(false);

  const load = async () => {
    const [b, l] = await Promise.all([
      api.get(`/batches/${id}`),
      api.get(`/lifecycle/batch/${id}`),
    ]);
    setBatch(b.data);
    setLifecycle(l.data);
  };

  useEffect(() => { load(); }, [id]);

  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/ai/hotspot/${id}`);
      setAnalysis(r.data.analysis);
    } catch { toast.error("AI failed"); }
    finally { setAiBusy(false); }
  };

  const recalc = async () => {
    setRecalcBusy(true);
    try {
      const r = await api.post(`/batches/${id}/recalculate`);
      setBatch(r.data);
      toast.success("PCF recalculated");
    } finally { setRecalcBusy(false); }
  };

  if (!batch) return <div className="text-slate-500">Loading…</div>;

  const breakdownData = Object.entries(batch.pcf_breakdown || {}).map(([k, v]) => ({
    stage: k.replace(/_/g, " "),
    tco2e: v,
  }));

  const Field = ({ label, value, accent }) => (
    <div className="border-b border-slate-100 py-3">
      <div className="overline text-slate-500">{label}</div>
      <div className={`font-mono text-sm font-semibold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );

  return (
    <div data-testid="batch-passport-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link to="/batches" data-testid="back-to-batches" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-emerald-800">
            <ArrowLeft className="w-3 h-3" /> All Batches
          </Link>
          <div className="overline text-emerald-800 mt-3">Batch Carbon Passport</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1 font-mono">{batch.batch_code}</h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="recalc-btn"
            onClick={recalc}
            disabled={recalcBusy}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border border-slate-300 hover:bg-slate-900 hover:text-white"
          >
            {recalcBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Recalculate
          </button>
          <button
            data-testid="ai-hotspot-btn"
            onClick={runAI}
            disabled={aiBusy}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900"
          >
            {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Hotspot Analysis
          </button>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div data-testid="stat-total-pcf" className="border border-slate-200 p-6">
          <div className="overline text-slate-500">Total PCF</div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{batch.total_pcf_tco2e?.toFixed(3)} <span className="text-base text-slate-500">tCO₂e</span></div>
        </div>
        <div className="border border-slate-200 p-6">
          <div className="overline text-slate-500">Per accepted note</div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{batch.pcf_per_note_kgco2e?.toFixed(5)} <span className="text-base text-slate-500">kgCO₂e</span></div>
        </div>
        <div className="border border-slate-200 p-6">
          <div className="overline text-slate-500">Per note-year</div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-2">{batch.pcf_per_note_year?.toFixed(5)}</div>
        </div>
        <div className="border border-slate-200 p-6">
          <div className="overline text-slate-500">Confidence</div>
          <div className="font-heading text-3xl font-extrabold text-emerald-800 mt-2 flex items-center gap-2"><Award className="w-6 h-6" />{batch.confidence_score}/100</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="border border-slate-200 p-6 bg-white">
          <div className="overline text-emerald-800 mb-4">Production Identity</div>
          <Field label="Denomination" value={`₱${batch.denomination}`} />
          <Field label="Substrate" value={batch.substrate} />
          <Field label="Site" value={batch.production_site} />
          <Field label="Printed Notes" value={batch.printed_notes?.toLocaleString()} />
          <Field label="Accepted Notes" value={batch.accepted_notes?.toLocaleString()} />
          <Field label="Spoilage Rate" value={`${batch.spoilage_rate?.toFixed(2)}%`} accent={batch.spoilage_rate > 5 ? "text-red-700" : "text-slate-900"} />
          <Field label="Note Life (yrs)" value={batch.estimated_note_life_years} />
          <Field label="Production Date" value={(batch.production_date || "").slice(0, 10)} />
          <Field label="Hotspot" value={batch.main_hotspot} accent="text-amber-700" />
          <Field label="Assurance" value={batch.assurance_status} />
        </div>

        {/* Breakdown chart */}
        <div className="lg:col-span-2 border border-slate-200 p-6 bg-white">
          <div className="overline text-slate-500">Lifecycle Breakdown</div>
          <div className="font-heading text-xl font-bold text-slate-900 mb-4">PCF by stage (tCO₂e)</div>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={breakdownData} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "#64748b" }} angle={-15} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="tco2e" radius={[2,2,0,0]}>
                  {breakdownData.map((d, i) => (
                    <Cell key={i} fill={d.stage === batch.main_hotspot.replace(/_/g, " ") ? "#B45309" : "#166534"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Lifecycle stages */}
          {lifecycle && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              <div className="overline text-slate-500 mb-3">Lifecycle Stage Emissions (modeled mapping)</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                {lifecycle.stage_emissions.map((s) => (
                  <div key={s.stage} className="border border-slate-200 p-3">
                    <div className="font-semibold text-slate-900">{s.stage}</div>
                    <div className="font-mono text-emerald-800">{s.tco2e} tCO₂e</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Analysis */}
      {(analysis || aiBusy) && (
        <div data-testid="ai-analysis-panel" className="border border-emerald-200 bg-emerald-50/40 p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-800" />
            <div className="overline text-emerald-800">AI Hotspot Analysis (Claude Sonnet 4.5)</div>
          </div>
          {aiBusy ? (
            <div className="flex items-center gap-2 text-slate-600 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing batch…</div>
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{analysis}</pre>
          )}
        </div>
      )}

      {/* Anomaly notice */}
      {batch.confidence_score < 75 && (
        <div className="border border-amber-300 bg-amber-50 p-4 text-sm flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900">Confidence below 75 — review supplier evidence and primary data.</div>
            <div className="text-amber-800 text-xs mt-1">Improve by uploading verified energy mix, ISO certificates, and supplier-specific PCF reports.</div>
          </div>
        </div>
      )}
    </div>
  );
}
